/**
 * Shared Orbit site content + route map.
 * Landing and detail pages read from here so promises stay consistent.
 */

export const ORBIT_CONFIG = {
  /** Digits only with country code — e.g. 9198XXXXXXXX */
  whatsappNumber:
    process.env.REACT_APP_ORBIT_WHATSAPP || '918500424835',
};

/** Honest scarcity — 5 families this month. Mission / new chapter — never school admission. */
export const SCARCITY = {
  families: 5,
  badge: '5 families · this chapter',
  short: '5 families · then next chapter',
  line: 'Orbit Month 1 is a free first chapter for 5 Hyderabad families (ages 5–8). When those 5 are in, this chapter closes.',
  miss: 'This chapter won’t wait forever.',
  cta: 'Start this month’s mission',
  ctaShort: 'Start the mission',
  nav: 'Start mission',
  formTitle: 'Step into Month 1',
  formLead: 'Age, area, and Saturday are pre-filled — adjust if needed, then send on WhatsApp.',
  /** Quiet context under the send CTA only — don’t stamp this elsewhere */
  formFine: 'Ages 5–8 · Hyderabad · free first month · open to 5 families.',
};

export const WA_PREFILL =
  'Hi! We want to start Orbit Month 1 with you (one of 5 families, ages 5–8, Hyderabad). Child age: __ Area: __';

export function whatsappHref(customText) {
  const text = encodeURIComponent(customText || WA_PREFILL);
  return `https://wa.me/${ORBIT_CONFIG.whatsappNumber}?text=${text}`;
}

/** Primary nav — useful first; About last (mission/vision lives there) */
export const ORBIT_NAV = [
  { label: 'What you get', to: '/orbit/what-you-get' },
  { label: 'Start', to: '/orbit/join' },
  { label: 'FAQ', to: '/orbit/faq' },
  { label: 'About', to: '/orbit/about' },
];

/**
 * Unschooling (the company) tries new bases of working with children.
 * Orbit is the first live version — more named bases are coming up.
 */
export const UNSCHOOLING_BASES = [
  {
    name: 'Orbit',
    status: 'live',
    meaning:
      'Monthly curiosity rhythm — growing by doing through kit, nearby missions, meetup, brave tries, and an achievements wall.',
  },
  {
    name: 'Spark',
    status: 'coming',
    meaning:
      'Engaging children — short, joyful ways to connect and play when you want lighter touchpoints, not a full month loop.',
  },
  {
    name: 'Grove',
    status: 'coming',
    meaning:
      'Growing children — longer arcs of overall development shaped around what your child cares about.',
  },
  {
    name: 'Compass',
    status: 'coming',
    meaning:
      'Teaching children gently — support when they want to learn something specific, still child-led, never school-style pressure.',
  },
  {
    name: 'Rise',
    status: 'coming',
    meaning:
      'Development made visible — how a child is becoming, celebrated as stories and skills, not marks or ranks.',
  },
];

export const LOOP_STEPS = [
  {
    name: 'Home',
    text: 'Kit arrives. Make, life skills, stories — child-led at home.',
    to: '/orbit/curiosity-kit',
  },
  {
    name: 'Outside',
    text: '2 nearby missions near your home: park, market, or nature.',
    to: '/orbit/nearby-missions',
  },
  {
    name: 'People',
    text: 'One easy Saturday meetup (~75 min). New kids, short play.',
    to: '/orbit/people-meetup',
  },
  {
    name: 'Brave',
    text: 'One stretch win — slightly hard, proudly done.',
    to: '/orbit/brave-win',
  },
  {
    name: 'Wall',
    text: 'An achievements wall of new skills, pride moments, and what they tried.',
    to: '/orbit/wins',
  },
];

export const GROWTH = [
  {
    muscle: 'Curiosity',
    how: 'Kit + make + story cards',
    notice: 'They start without you pushing',
    to: '/orbit/curiosity-kit',
  },
  {
    muscle: 'Courage',
    how: 'Brave challenge + meetup',
    notice: 'They try something slightly hard',
    to: '/orbit/brave-win',
  },
  {
    muscle: 'Real-world sense',
    how: 'Nearby missions',
    notice: 'They notice the world outside the house',
    to: '/orbit/nearby-missions',
  },
  {
    muscle: 'Social ease',
    how: 'One meetup / shared play',
    notice: 'They play with a new kid',
    to: '/orbit/people-meetup',
  },
];

export const MONTH_GET = [
  {
    slug: 'curiosity-kit',
    icon: '📦',
    title: 'Curiosity Kit',
    text: 'Home box to start: make materials + child-led cards (life, story, body, brave)',
    to: '/orbit/curiosity-kit',
  },
  {
    slug: 'nearby-missions',
    icon: '🗺️',
    title: '2 Nearby Missions',
    text: 'Short park / market / nature outings near home — you go with them',
    to: '/orbit/nearby-missions',
  },
  {
    slug: 'people-meetup',
    icon: '🤝',
    title: '1 People Meetup',
    text: '~75 min Saturday play with new kids — local spot, parent stays',
    to: '/orbit/people-meetup',
  },
  {
    slug: 'brave-win',
    icon: '⚡',
    title: '1 Brave Win',
    text: 'One kindly stretch they can try and feel proud of — never graded',
    to: '/orbit/brave-win',
  },
  {
    slug: 'wins',
    icon: '✨',
    title: 'Achievements Wall',
    text: 'Skills tried, pride moments, achievements — stories, not marks',
    to: '/orbit/wins',
  },
];

/** Full-month picture on What you get */
export const MONTH_JOURNEY = [
  {
    title: 'How it starts',
    text: 'Send on WhatsApp from Start → we hand-deliver the Curiosity Kit.',
  },
  {
    title: 'Core rhythm (not a cage)',
    text: 'Home → outside → people → brave → achievements wall. Themes stretch around what your child cares about.',
  },
  {
    title: 'How it closes',
    text: 'Celebrate on the achievements wall — then you choose whether Month 2 feels right. No pressure.',
  },
];

export const EXPERIENCE_EXAMPLES = [
  {
    id: 'kit',
    icon: '📦',
    title: 'Curiosity Kit — example month',
    theme: 'Theme: Nearby World',
    to: '/orbit/curiosity-kit',
    items: [
      'Make: build a tiny cardboard “street” of places near home (shop, park, tree).',
      'Life skill: help set the table or wash fruit — one real job, finished.',
      'Story: a short culture/story card about a place or festival.',
      'Body: balance walk / stretch card for 5–10 minutes of play.',
      'Brave: one small stretch (e.g. ask a neighbour a friendly question, with you nearby).',
    ],
  },
  {
    id: 'missions',
    icon: '🗺️',
    title: 'Nearby missions — what you’ll do',
    theme: '15–40 min · park, market, or nature near you',
    to: '/orbit/nearby-missions',
    items: [
      'Park: find 5 textures (bark, leaf, sand, metal, stone) and name how they feel.',
      'Market: notice 3 colours of fruit/veg, greet one vendor, count steps back home.',
      'Nature strip / lake edge: listen for 2 bird sounds, collect 3 fallen leaves (no picking live plants).',
      'Always nearby — not a far city day trip. Parent goes with the child.',
    ],
  },
  {
    id: 'meetup',
    icon: '🤝',
    title: 'People meetup — what to expect',
    theme: '~75 min Saturday · parent stays',
    to: '/orbit/people-meetup',
    items: [
      'Warm hello circle — names + one favourite thing this week.',
      'Shared make or game table (build, sort, race, invent a rule).',
      'Partner play: two kids try a short challenge together.',
      'Brave share (optional): show something they made or a mission photo — only if they want.',
      'You stay nearby; we keep it light, local, and age-fit for 5–8.',
    ],
  },
];

export const PILLARS = [
  {
    id: 'kit',
    icon: '📦',
    title: 'Curiosity Kit',
    color: '#ffd23f',
    tagBg: '#e8a800',
    to: '/orbit/curiosity-kit',
    blurb:
      'Hands-on materials plus self-led challenges at home — open the box, explore, no tutor required.',
    variations: [
      'Nearby World kit (street map make + market story)',
      'Sound & Music kit (homemade shaker + listen walk card)',
      'Green Hands kit (seed cup + water-care life skill)',
      'Body Brave kit (balance path + stretch challenge)',
    ],
  },
  {
    id: 'missions',
    icon: '🗺️',
    title: 'Outside Missions',
    color: '#ff5a45',
    tagBg: '#e8432f',
    to: '/orbit/nearby-missions',
    blurb:
      '2 real-world missions each month near your home: park, market, or nature — not far city trips.',
    variations: [
      'Park texture hunt',
      'Market colour & greet mission',
      'Nature listen-and-collect (fallen only)',
      'Road-sense walk: safe crossing practice with you',
      'Neighbourhood “helper spots” map (park, clinic, shop)',
    ],
  },
  {
    id: 'place',
    icon: '🏛️',
    title: 'Place Experience',
    color: '#3db8ff',
    tagBg: '#1a9fe8',
    to: '/orbit/ways/place',
    blurb:
      'In the pilot, the meetup place is the place experience — a new environment with purpose, without a separate venue day.',
    variations: [
      'Community park pavilion',
      'Quiet courtyard / society lawn',
      'Library kids’ corner (future)',
      'Garden / nursery visit (future month)',
    ],
  },
  {
    id: 'people',
    icon: '🤝',
    title: 'People & Play',
    color: '#2ecf7a',
    tagBg: '#1aad5e',
    to: '/orbit/people-meetup',
    blurb:
      'One peer meetup — meet new kids, try something together, practice social courage. Parent stays (ages 5–8).',
    variations: [
      'Build-together table',
      'Invent-a-game pairs',
      'Story circle + act one line',
      'Dance freeze / rhythm play',
      'Help-a-friend tidy challenge',
    ],
  },
  {
    id: 'body',
    icon: '🤸',
    title: 'Body & Movement',
    color: '#ff8a3d',
    tagBg: '#e86f1a',
    to: '/orbit/ways/body',
    blurb:
      'Active play at home and in the meetup — run, climb, balance — energy and confidence in the body.',
    variations: [
      'Balance beam (tape line)',
      'Animal moves relay',
      'Stretch & breathe card',
      'Dance freestyle minute',
      'Climb-and-rest park play',
    ],
  },
  {
    id: 'make',
    icon: '🎨',
    title: 'Make & Create',
    color: '#5ad4ff',
    tagBg: '#1a9fe8',
    to: '/orbit/ways/make',
    blurb:
      'Build or make art until there is something real to finish and show with pride.',
    variations: [
      'Cardboard street / city',
      'Paper instruments',
      'Clay creatures',
      'Recycled sculpture',
      'Comic of today’s mission',
    ],
  },
  {
    id: 'life',
    icon: '🍳',
    title: 'Life Skills',
    color: '#ffd23f',
    tagBg: '#e8a800',
    to: '/orbit/ways/life',
    blurb:
      'Small real jobs — help cook, organize, care for something — independence that sticks.',
    variations: [
      'Set the table / clear dishes',
      'Wash fruit / simple snack help',
      'Water a plant on a schedule',
      'Pack their own outing bag',
      'Sort recycling with you',
    ],
  },
  {
    id: 'culture',
    icon: '📖',
    title: 'Culture & Stories',
    color: '#2ecf7a',
    tagBg: '#1aad5e',
    to: '/orbit/ways/culture',
    blurb:
      'A story or culture card in the kit — light in the pilot, not a far outing. Later months stay open to whatever draws the child.',
    variations: [
      'Festival / food story card',
      'Listen-and-clap rhythm spark',
      'Neighbourhood helper roles',
      'Local place or tree story',
      'Playful movement from a tradition (not a performance)',
    ],
  },
  {
    id: 'brave',
    icon: '⚡',
    title: 'Brave Challenge',
    color: '#ff5a45',
    tagBg: '#e8432f',
    to: '/orbit/brave-win',
    blurb:
      'One stretch challenge each month — something slightly scary, proudly completed. Never graded.',
    variations: [
      'Say hi to a new kid first',
      'Ask a vendor one question',
      'Share a make in the circle (optional)',
      'Try a new food texture',
      'Lead a 30-second game rule',
    ],
  },
  {
    id: 'wins',
    icon: '✨',
    title: 'Achievements Wall',
    color: '#3db8ff',
    tagBg: '#1a9fe8',
    to: '/orbit/wins',
    blurb:
      'An achievements wall of new skills, pride moments, and what they explored — enthusiastic, never marks. Growth you can feel, not score.',
    variations: [
      '“I made this” photo wall',
      'New skill stickers (tried / finished / invented)',
      'Pride note from parent (optional, in their words)',
      'Mission map stamps (places they noticed)',
      'Brave badge of the month (story, not a grade)',
    ],
  },
];

export const FAQS = [
  {
    group: 'The idea',
    q: 'What is Orbit trying to do?',
    a: 'A different path if you’re tired of pressure-first school — explore, invent, feel proud, with support beyond parents alone. See About for the full why.',
  },
  {
    group: 'The idea',
    q: 'Is this a school?',
    a: 'No. No syllabus, live classes, tutors, homework, or exam scores.',
  },
  {
    group: 'What you get',
    q: 'What does one month include?',
    a: 'Open What you get for the full map — kit, missions, meetup, brave stretch, achievements wall.',
  },
  {
    group: 'What you get',
    q: 'Can it change for my child?',
    a: 'Yes. The core stays honest so you know what you’re joining. Share what they love and we’ll try to stretch within the month.',
  },
  {
    group: 'What you get',
    q: 'What’s in the Curiosity Kit?',
    a: 'Open the Curiosity Kit page for the full unpack — make materials, child-led cards, and a short parent note.',
  },
  {
    group: 'What you get',
    q: 'What will my child “achieve”?',
    a: 'Pride moments on an achievements wall — stories and skills, never marks or percentages. One example: “I made this” from the kit.',
  },
  {
    group: 'Joining',
    q: 'Who is it for?',
    a: 'Parents ready for a first Orbit chapter with a child who’d enjoy kit + nearby missions + one Saturday meetup. Ages, area, and Saturday are on the Start form.',
  },
  {
    group: 'Joining',
    q: 'Do I have to teach?',
    a: 'No. Cards are child-led. You support; you don’t run a classroom. You stay at the meetup while we host the flow.',
  },
  {
    group: 'Joining',
    q: 'Where is the meetup?',
    a: 'Near your cluster after families join — about 30 minutes travel. Parent stays. Not a city-wide trek.',
  },
  {
    group: 'Joining',
    q: 'How do we start — and finish the month?',
    a: 'Details + WhatsApp on Start → kit delivery → run the month → celebrate → choose Month 2.',
  },
];

/**
 * Short, scannable detail pages (no long essays, no sidebar clutter).
 * Keys = URL slug under /orbit/:slug
 */
export const ORBIT_PAGES = {
  'curiosity-kit': {
    layout: 'offer',
    accent: '#ffb020',
    icon: '📦',
    title: 'Curiosity Kit',
    line: 'The home box that starts the month — make something real, then explore with child-led cards.',
    inBox: [
      { icon: '🎨', label: 'Make materials' },
      { icon: '🧾', label: 'Make card' },
      { icon: '🍳', label: 'Life-skill card' },
      { icon: '📖', label: 'Story card' },
      { icon: '🤸', label: 'Body card' },
      { icon: '⚡', label: 'Brave card' },
    ],
    looksLike: [
      'Build a tiny cardboard street of places near home',
      'One real home job — set the table or wash fruit',
      'A short story or culture spark',
      '5–10 minutes of balance / stretch / movement play',
      'One small brave try (optional if they’re not ready)',
    ],
    you: 'Hand the kit over. Stay nearby. Answer if asked — don’t run a class. Celebrate trying, not neatness.',
    they: 'Choose what to open first. Make, mess, invent, redo. Decide when it feels done.',
    not: ['No tutors', 'No homework', 'No marks', 'No far travel'],
    next: { label: 'Nearby Missions', to: '/orbit/nearby-missions' },
  },
  'nearby-missions': {
    layout: 'offer',
    accent: '#ff5a45',
    icon: '🗺️',
    title: 'Nearby Missions',
    line: 'Two short real-world outings near your home — park, market, or nature. You go with them.',
    inBox: [
      { icon: '1️⃣', label: 'Mission card 1' },
      { icon: '2️⃣', label: 'Mission card 2' },
      { icon: '⏱️', label: '15–40 minutes' },
      { icon: '👨‍👩‍👧', label: 'You go with them' },
    ],
    looksLike: [
      'Park: find 5 textures and name how they feel',
      'Market: notice 3 colours, greet one vendor',
      'Nature edge: listen for birds; collect fallen leaves only',
      'Always nearby — not a city day trip',
    ],
    you: 'Pick a nearby spot and a time that fits. Keep it fun. Leave before meltdown. Ask “what do you notice?”',
    they: 'Look, listen, choose, greet, explore — lead a little when they’re ready.',
    not: ['No far city trips', 'No staff escort', 'No worksheets'],
    next: { label: 'People Meetup', to: '/orbit/people-meetup' },
  },
  'people-meetup': {
    layout: 'offer',
    accent: '#2ecf7a',
    icon: '🤝',
    title: 'People Meetup',
    line: 'One Saturday (~75 min) near your cluster — new kids, shared play, parent stays.',
    inBox: [
      { icon: '👋', label: 'Hello circle' },
      { icon: '🎲', label: 'Shared play' },
      { icon: '👫', label: 'Partner try' },
      { icon: '🎤', label: 'Optional share' },
    ],
    looksLike: [
      'Names + one favourite thing this week',
      'Shared table: build, sort, race, or invent a rule',
      'Pairs try a short challenge together',
      'Optional: show something made — only if they want',
    ],
    you: 'Arrive on time and stay nearby. Let us host. Step in only if your child needs you. No comparing kids.',
    they: 'Say hi when ready. Play, invent, rest, try again.',
    not: ['Not drop-and-leave', 'Not a performance', 'Not a class'],
    next: { label: 'Brave Win', to: '/orbit/brave-win' },
  },
  'brave-win': {
    layout: 'offer',
    accent: '#ff5a45',
    icon: '⚡',
    title: 'Brave Win',
    line: 'One kindly stretch each month — something slightly hard, proudly tried, never scored.',
    inBox: [
      { icon: '🃏', label: 'One brave card' },
      { icon: '🎚️', label: 'Sized for ages 5–8' },
      { icon: '⏭️', label: 'Skip if not today' },
    ],
    looksLike: [
      'Say hi to a new kid first',
      'Ask a vendor one question',
      'Share a make in the circle (optional)',
      'Try a new food texture or lead a tiny game rule',
    ],
    you: 'Cheer the attempt, not perfection. Never force a public share.',
    they: 'Pick a brave size that fits today. Feel proud of trying.',
    not: ['No grades', 'No forced stage', 'No fear challenges'],
    next: { label: 'Achievements Wall', to: '/orbit/wins' },
  },
  wins: {
    layout: 'offer',
    accent: '#3db8ff',
    icon: '✨',
    title: 'Achievements Wall',
    line: 'How the month closes — skills, pride moments, and achievements as stories, not a report card.',
    inBox: [
      { icon: '🛠️', label: 'Skills tried' },
      { icon: '📸', label: 'Pride moments' },
      { icon: '🗺️', label: 'Places noticed' },
      { icon: '⚡', label: 'Brave story' },
    ],
    looksLike: [
      '“I made this” from the kit',
      'Greeted someone new on a mission',
      'Played with a new kid at meetup',
      'A brave try they want to remember — narrative, not a grade',
    ],
    you: 'Notice out loud what grew. Share in your own words only if you want — never mandatory.',
    they: 'Point to what they’re proud of. Invent the next try.',
    not: ['No marks', 'No ranks', 'No % scores', 'No forced feedback gate'],
    next: { label: 'Start this month’s mission', to: '/orbit/join' },
  },
  about: {
    layout: 'mission',
    accent: '#ff5a45',
    icon: '🌟',
    title: 'About',
    line: 'Why we exist — overall development through growing by doing.',
    mission:
      'Help children grow in their own way — overall development you recognise from the books and ideas about learning that stay with you: curiosity, courage, real-world sense, and becoming who they are. Not a rigid curriculum. Support beyond parents alone.',
    vision:
      'Kids who invent, explore, and feel happy — without society’s scoreboard. Customised around the child. Free to follow whatever draws them next.',
    next: { label: 'See what you get', to: '/orbit/what-you-get' },
  },
  join: {
    layout: 'join',
    accent: '#ffd23f',
    icon: '🚀',
    title: 'Step into Month 1',
    line: 'A few details, then WhatsApp — we reply from there.',
    steps: [
      'Share a few details below',
      'Send on WhatsApp',
      'We hand-deliver the Curiosity Kit',
      'Run the month · celebrate · decide on Month 2',
    ],
    next: { label: 'Browse what you get', to: '/orbit/what-you-get' },
  },
  faq: {
    layout: 'faq',
    accent: '#3db8ff',
    icon: '💬',
    title: 'Questions parents ask',
    line: 'Short answers — pages above have the full picture.',
    next: { label: 'See what you get', to: '/orbit/what-you-get' },
  },
  'what-you-get': {
    layout: 'map',
    accent: '#2ecf7a',
    icon: '🧭',
    title: 'What you get',
    line: 'How the month starts, runs, and closes — then tap any piece.',
    next: { label: 'Start this month’s mission', to: '/orbit/join' },
  },
};

export function getOrbitPage(slug) {
  return ORBIT_PAGES[slug] || null;
}

export function getPillar(id) {
  return PILLARS.find((p) => p.id === id) || null;
}
